// ------------------------------------------------------------------------------------------------
import { ReactNode, Component, CSSProperties, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import type { ExcelOptions } from './TimeSeriesStructs';
import { getToolbarHeight } from './ChartHelpers';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { ToolbarButton } from '../../../Tables/ReactTable';
import { handleExcelDownloadClick } from "../../../Chart/ChartJsUtils";
import { ChartPortData, ChartsJsonSchemeValidator, OutputChartData, ChartDateRange, chartDateRangeValidator } from './ChartsJsonScheme';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { DateFormat, Formatters } from '@Utils/Formats';
import { getRandomSmallKey, isDefined, IsNullOrEmpty, isStringEmpty } from '@Utils/Micro';
import { SettingsList } from '@Primitive/SettingsList';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import RsDateRangePicker from '@Primitive/RsDateRangePicker';
import { eIconType, SettingsElementSlider, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { ToolbarIconInfo, ToolbarIconInfo_ButtonRender, ToolbarIconInfo_GetSettingList } from '@Primitive/ToolbarIconButton';
import { WebNode } from '@API/WebNode';
import { Plegma } from '@API/Api';
import { WebThing, WebThingPort } from '@API/WebThing';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
import Toggle from "@Primitive/Toggle";

const ChartJsLine = lazy(() => import("./Exports/Line"));
// ------------------------------------------------------------------------------------------------------
const enum LineChartKeys { onToolbarIconClick = 'onToolbarIconClick' };
// ------------------------------------------------------------------------------------------------
export function applyStatsToDatasets(
    data: ChartPortData,
    minColor: string,
    maxColor: string,
    borderWidth: number
): ChartPortData {

    if (isDefined(data))
        _.forEach(data.datasets, v => {
            // Initialize stats, indexes and colors.
            let max = Number.MIN_VALUE,
                min = Number.MAX_VALUE,
                avg = 0,
                sum = 0,
                maxIdx,
                minIdx;

            const color = v.pointBackgroundColor,
                transparentColor = 'rgba(0, 0, 0, 0)';

            // Find min/max values.
            _.forEach(v.data, (_v, i) => {
                if (_v > max) {
                    max = _v;
                    maxIdx = i;
                }
                if (_v < min) {
                    min = _v;
                    minIdx = i;
                }
                sum += _v;
            });

            // Mark min/max points with selected colors.
            v.pointBackgroundColor = _.map(_.range(0, v.data.length), (_v) => {
                return _v == maxIdx
                    ? maxColor : _v == minIdx
                        ? minColor : color;
            }) as string[];

            // Add new dataset for avg.
            avg = sum / v.data.length;
            data.datasets.push({
                label: 'Avg: ' + avg.toFixed(1),
                data: _.map(_.range(0, v.data.length), () => { return avg; }),
                backgroundColor: transparentColor,
                borderWidth: borderWidth,
                borderColor: v.borderColor,
                borderDash: [2],
                pointBackgroundColor: transparentColor,
                pointBorderColor: transparentColor,
                pointHoverBackgroundColor: transparentColor,
                pointHoverBorderColor: transparentColor,
                pointRadius: 0,
                pointHitRadius: 0,
                pointHoverRadius: 0
            });
        });
    return data;
}
// ------------------------------------------------------------------------------------------------
const enum PortKeys {
    Data = "data",
    DateRange = 'daterange',
    SelectedRange = "SelectedRange",
    RealTime = "RealTime",
    SelectedData = "SelectedData"
}
// ------------------------------------------------------------------------------------------------
export interface LineChartState {
    Data: ChartPortData;
    EnabledAxis: { [id: string]: boolean };
    RealTimeData: boolean;
    layoutPaddingLeft: number;
    DateRange: ChartDateRange;
}
// ------------------------------------------------------------------------------------------------
export interface LineChartData {
    showStats: boolean;
    minColor: string;
    maxColor: string;
    avgLineThickness: number;
    showDateRange: boolean;
    initialDateRangeDays: number;
    timePicker: boolean;
    timePickerIncrement: number;
    timePickerInitialRange: number;
    axisBeginAtZero: boolean;
    initialRealtimeData: boolean;
    showRealTimeFlag: boolean;
    showLegend: boolean;
    legendPosition: 'left' | 'right' | 'top' | 'bottom';
    legendUsePointStyle: boolean;
    legendFontColor: string;
    legendFontSize: number;
    excelDownload: boolean;
    excelOptions: ExcelOptions;

    //Toolbar
    defaultIconsColor?: string;
    customToolbarIcons: ToolbarIconInfo[];
    toolbarIconSize?: number;
    toolbarHeight?: number;
}
// ------------------------------------------------------------------------------------------------
interface IDataSetup {
    data: ChartPortData;
    enabledAxis: { [key: string]: boolean };
    paddingLeft: number;
}
// ------------------------------------------------------------------------------------------------
class LineChart extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<LineChartData>, LineChartState> {
    // ------------------------------------------------------------------------------------------------
    private static _DefaultPaddingLeft: number = 0;
    // ------------------------------------------------------------------------------------------------
    private _xAxes: Chart.CommonAxe[] = [];
    private _yAxes: Chart.CommonAxe[] = [];
    // ------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<LineChartData>) {
        super(props);

        // Init state.
        const setup = this.defaultDataSetup;
        this.state = {
            Data: null,
            EnabledAxis: setup.enabledAxis,
            RealTimeData: this.props.WidgetData.initialRealtimeData,
            layoutPaddingLeft: setup.paddingLeft,
            DateRange: {
                StartDate: null,
                EndDate: null
            }
        };
    }
    // --------------------------------------------------------------------------------------------
    get iconToolbarIconsProps(): CSSProperties {
        return {
            padding: 0,
            margin: '0 4px',
            width: this.props.WidgetData.toolbarHeight,
            height: this.props.WidgetData.toolbarHeight,
            lineHeight: this.props.WidgetData.toolbarHeight + "px",
            fontSize: this.props.WidgetData.toolbarIconSize,
            cursor: "pointer"
        };
    }
    // --------------------------------------------------------------------------------------------
    get defaultDataSetup(): IDataSetup {
        return {
            data: null,
            enabledAxis: {},
            paddingLeft: LineChart._DefaultPaddingLeft
        };
    }
    // --------------------------------------------------------------------------------------------
    componentDidMount() {
        this.UpdateData(this.props);
    }
    // --------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<LineChartData>) {
        if (prevProps.PortVersion != this.props.PortVersion ||
            !_.isEqual(prevProps.WidgetDefaultValues, this.props.WidgetDefaultValues))
            this.UpdateData(this.props);
    }
    // --------------------------------------------------------------------------------------------
    onToolbarIconClick = (iconInfo: ToolbarIconInfo) => this.props.SendPortEvent(LineChartKeys.onToolbarIconClick, iconInfo.id);
    // --------------------------------------------------------------------------------------------
    setDefaultData(props: WidgetsThingsStructs.PartialIWidgetThingsProps<LineChartData>) {
        const defaultValues = props.WidgetDefaultValues;
        if (_.has(defaultValues, PortKeys.Data)) {
            // Data setup.
            let setup = this._dataSetup(_.cloneDeep(defaultValues[PortKeys.Data]));
            if (!isDefined(setup))
                setup = this.defaultDataSetup;

            this.setState({
                Data: setup.data,
                EnabledAxis: setup.enabledAxis,
                layoutPaddingLeft: setup.paddingLeft
            });
        }
    }
    // --------------------------------------------------------------------------------------------
    UpdateData(props: WidgetsThingsStructs.PartialIWidgetThingsProps<LineChartData>) {
        let hasDataPortState = false;
        if (_.has(props.PortsStates, PortKeys.Data)) {
            let port = props.PortsStates[PortKeys.Data];
            if (isDefined(port.State)) {
                hasDataPortState = true;
                let data: ChartPortData = port.State;

                // if data is not valid; show proper message in render
                if (port.LastStateStatus == StateStatus.NoData) {
                    this.setDefaultData(props);
                } else if (port.LastStateStatus != StateStatus.Valid) {
                    this.setState({ Data: null });
                } else {
                    // Date setup.
                    let setup = this._dataSetup(_.cloneDeep(data));
                    if (!isDefined(setup))
                        setup = this.defaultDataSetup;

                    // Update state.
                    this.setState({
                        Data: setup.data,
                        EnabledAxis: setup.enabledAxis,
                        layoutPaddingLeft: setup.paddingLeft
                    });
                }
            }
        }

        if (!hasDataPortState) {
            this.setDefaultData(props);
        }

        let hasDateRangePortState = false;
        if (_.has(props.PortsStates, PortKeys.DateRange)) {
            let dateRangePort = props.PortsStates[PortKeys.DateRange];

            // is it touched?
            if (!IsNullOrEmpty(dateRangePort.State)) {
                hasDateRangePortState = true;
                let dateRange: ChartDateRange;

                // set the date range
                switch (dateRangePort.LastStateStatus) {
                    case StateStatus.Valid:
                        dateRange = _.cloneDeep(dateRangePort.State);
                        break;
                    case StateStatus.NoData:
                        dateRange = _.cloneDeep(props.WidgetDefaultValues[PortKeys.DateRange]);
                        break;
                    default:
                        break;
                }
                this.setState({ DateRange: dateRange });
            }
        }

        // if no data then try to get the default date range
        if (!hasDateRangePortState) {
            if (_.has(props.WidgetDefaultValues, PortKeys.DateRange)) {
                let dateRange = _.cloneDeep(props.WidgetDefaultValues[PortKeys.DateRange]);
                this.setState({ DateRange: dateRange as ChartDateRange });
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    private _dataSetup(data: ChartPortData): IDataSetup {
        let obj: IDataSetup = {
            data: data,
            enabledAxis: {},
            paddingLeft: LineChart._DefaultPaddingLeft
        };

        if (isDefined(data)) {
            const allowedAxes = [
                "y1",
                "y2",
                "log-y1",
                "log-y2",
                "cat-y-left",
                "cat-y-right"
            ];
            _.forEach(data.datasets, d => {
                if (!IsNullOrEmpty(d.yAxisID) &&
                    allowedAxes.indexOf(d.yAxisID))
                    obj.enabledAxis[d.yAxisID] = true;
            });
            _.forEach(data.yLabels, yl => {
                if (!isStringEmpty(yl)) {
                    // 3 pixels per char?
                    const pixelsLength = String(yl).length * 3 + 18;
                    if (obj.paddingLeft < pixelsLength)
                        obj.paddingLeft = pixelsLength;
                }
            });

            const widgetData = this.props.WidgetData;
            if (widgetData.showStats)
                data = applyStatsToDatasets(
                    data,
                    widgetData.minColor,
                    widgetData.maxColor,
                    widgetData.avgLineThickness,
                );

            // Collect axes IDs from configuration.
            let axesIDs = _.compact(_.concat(
                _.map(data.xAxes, v => v.id),
                _.map(data.yAxes, v => v.id),
                allowedAxes
            ));

            const unlinkAxis = (id: string) => {
                return isDefined(id) && _.indexOf(axesIDs, id) == -1;
            };

            // Remove unlinked axes.
            _.forEach(data.datasets, v => {
                if (unlinkAxis(v.xAxisID)) delete v.xAxisID;
                if (unlinkAxis(v.yAxisID)) delete v.yAxisID;
            });

            // Remove any x and y axes configuration.
            obj.data = _.omit(data, ['xAxes', 'yAxes']);

            // Set the needed scale options.
            const updateScaleOpts = (v: Chart.CommonAxe) => {
                if (!isDefined(v.ticks))
                    v.ticks = {
                        beginAtZero:
                            widgetData.axisBeginAtZero
                    };
                else if (!isDefined(v.ticks.beginAtZero))
                    v.ticks.beginAtZero =
                        widgetData.axisBeginAtZero;
            };

            // Collect axes IDs from datasets.
            axesIDs = _.compact(_.concat(
                _.map(data.datasets, v => v.xAxisID),
                _.map(data.datasets, v => v.yAxisID)
            ));

            // Keep linked axes.
            this._xAxes = _.filter(
                data.xAxes,
                v => _.indexOf(axesIDs, v.id) > -1
            );
            this._yAxes = _.filter(
                data.yAxes,
                v => _.indexOf(axesIDs, v.id) > -1
            );

            // Update the scale options.
            _.forEach(this._xAxes, v => updateScaleOpts(v));
            _.forEach(this._yAxes, v => updateScaleOpts(v));
        }
        return obj;
    }
    // --------------------------------------------------------------------------------------------
    private _sendDaterangePortEvent = (startDay: string, endDay: string) => {
        // Send port event msg for the selected range.
        this.props.SendPortEvent(PortKeys.SelectedRange, JSON.stringify({
            StartDate: startDay,
            EndDate: endDay
        } as ChartDateRange));
    }
    // --------------------------------------------------------------------------------------------
    render() {
        const {
            Data,
            RealTimeData,
            DateRange,
            EnabledAxis,
            layoutPaddingLeft
        } = this.state;

        const {
            NodeKey,
            ThingKey,
            WidgetData,
            SendPortEvent,
            width: totalWd,
            height: totalHt,
            LocalizationMap,
            WidgetDefaultValues
        } = this.props;

        //todo show be taken from settings
        let toolbarButtons = ToolbarButton.none;
        const showToolbarIcons = (
            isDefined(WidgetData.customToolbarIcons) &&
            WidgetData.customToolbarIcons.length > 0
        ) || toolbarButtons != ToolbarButton.none;

        const pad = WidgetsThingsStructs.defaultWidgetContainerPadding;
        const hideToolbar = !(WidgetData.showDateRange || WidgetData.excelDownload || showToolbarIcons);
        const toolbarHt = hideToolbar ? 0 : getToolbarHeight(totalHt);
        const width = totalWd - 2 * pad;
        const height = totalHt - 2 * pad - toolbarHt;

        let label = LocalizationMap['RealTime'];
        if (!isDefined(label) || IsNullOrEmpty(label))
            label = 'Real Time';

        let rangeValue = null;
        if (DateRange != null && DateRange.StartDate != null && DateRange.EndDate != null)
            rangeValue = [new Date(DateRange.StartDate), new Date(DateRange.EndDate)];

        return <div style={{ padding: pad }}>
            {hideToolbar ? null
                : <div
                    style={{
                        padding: 0,
                        height: toolbarHt,
                        width: totalWd,
                        backgroundColor: 'transparent',
                        display: "flex",
                        alignContent: "center",
                        justifyContent: "flex-end",
                        alignItems: "center"
                    }}>
                    {WidgetData.showDateRange ?
                        <div style={{ display: "flex", alignContent: "center" }}>
                            {!WidgetData.showRealTimeFlag
                                ? null
                                : <div style={{
                                    display: 'flex',
                                    height: toolbarHt,
                                    fontSize: "12px",
                                    paddingRight: 10,
                                    paddingTop: 3
                                }}>
                                    <Toggle
                                        key={"teal-time"}
                                        toggled={RealTimeData}
                                        label={label}
                                        onToggle={(isInputChecked: boolean) => {
                                            this.setState({ RealTimeData: isInputChecked });
                                            SendPortEvent(PortKeys.RealTime, isInputChecked);
                                        }} />
                                </div>
                            }
                            {(() => {
                                if (!(RealTimeData && WidgetData.showRealTimeFlag))
                                    return <RsDateRangePicker
                                        transparent
                                        navEnabled
                                        usePredefinedRanges
                                        size='sm'
                                        cleanable={false}
                                        localizationKey={NodeKey}
                                        hasTime={WidgetData.timePicker}
                                        //timePickerIncrement={WidgetData.timePickerIncrement}
                                        initialDaysDiff={WidgetData.initialDateRangeDays}
                                        initialHoursDiff={WidgetData.timePickerInitialRange}
                                        value={rangeValue}
                                        onChange={undefined}
                                        onChangeBackwardCompatible={this._sendDaterangePortEvent} />
                            })()}
                        </div>
                        : null}
                    {WidgetData.excelDownload ?
                        <span>
                            <i
                                className={BoardTheming.GetTheme(this.props.NodeKey).widgets.charts.excelIcon}
                                style={{ color: WidgetData.defaultIconsColor, ...this.iconToolbarIconsProps }}
                                onClick={() => {
                                    handleExcelDownloadClick(
                                        WidgetData.excelOptions.fileName,
                                        WebNode
                                            .GetInstanceKey(NodeKey)
                                            .StateManager
                                            .GetState(ThingKey),
                                        PortKeys.Data,
                                        WidgetDefaultValues
                                    );
                                }} />
                        </span>
                        : null}
                    {showToolbarIcons ?
                        ToolbarIconInfo_ButtonRender(
                            WidgetData.customToolbarIcons,
                            [],
                            [],
                            this.iconToolbarIconsProps,
                            this.onToolbarIconClick,
                            'charts'
                        )
                        : null}
                </div>}
            {!isDefined(Data)
                ? WidgetsThingsStructs.DefaultMessageOnNoData(width, height)
                : <Suspense fallback={<LazyLoadingAnimation />}>
                    <ChartJsLine
                        id={ThingKey}
                        width={width}
                        height={height}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            animation: null,
                            scales: {
                                xAxes: this._xAxes,
                                yAxes: [
                                    {
                                        id: "y1",
                                        position: "left",
                                        type: "linear",
                                        display: _.isEmpty(EnabledAxis) || _.has(EnabledAxis, "y1"),
                                        ticks: {
                                            beginAtZero: WidgetData.axisBeginAtZero
                                        }
                                    },
                                    {
                                        id: "y2",
                                        position: "right",
                                        type: "linear",
                                        display: _.has(EnabledAxis, "y2"),
                                        ticks: {
                                            beginAtZero: WidgetData.axisBeginAtZero
                                        }
                                    },
                                    {
                                        id: "log-y1",
                                        position: "left",
                                        type: "logarithmic",
                                        display: _.has(EnabledAxis, "log-y1"),
                                        ticks: {
                                            beginAtZero: WidgetData.axisBeginAtZero
                                        }
                                    },
                                    {
                                        id: "log-y2",
                                        position: "right",
                                        type: "logarithmic",
                                        display: _.has(EnabledAxis, "log-y2"),
                                        ticks: {
                                            beginAtZero: WidgetData.axisBeginAtZero
                                        }
                                    },
                                    {
                                        id: "cat-y-left",
                                        type: 'category',
                                        position: "left",
                                        display: _.has(EnabledAxis, "cat-y-left"),
                                        scaleLabel: {
                                            display: true
                                        },
                                        ticks: {
                                            reverse: true
                                        }
                                    },
                                    {
                                        id: "cat-y-right",
                                        type: 'category',
                                        position: "right",
                                        display: _.has(EnabledAxis, "cat-y-right"),
                                        scaleLabel: {
                                            display: true
                                        },
                                        ticks: {
                                            reverse: true
                                        }
                                    },
                                    ...this._yAxes
                                ]
                            },
                            legend: {
                                display: WidgetData.showLegend,
                                position: WidgetData.legendPosition,
                                labels: {
                                    fontSize: WidgetData.legendFontSize,
                                    fontColor: WidgetData.legendFontColor,
                                    usePointStyle: WidgetData.legendUsePointStyle
                                }
                            },
                            onClick: (evt, elems) => {
                                // find values for each dataset
                                if (elems.length > 0) {
                                    var outputData: OutputChartData[] = [];
                                    for (var i = 0; i < elems.length; i++) {
                                        let el: any = elems[i];
                                        if (isDefined(el._datasetIndex) && isDefined(el._index)) {
                                            outputData.push({
                                                Dataset: this.state.Data.datasets[el._datasetIndex].label,
                                                Label: this.state.Data.labels[el._index],
                                                Value: this.state.Data.datasets[el._datasetIndex].data[el._index],
                                            });
                                        }
                                    }

                                    // send port event
                                    SendPortEvent(PortKeys.SelectedData, JSON.stringify(outputData));
                                }
                            },
                            // Layout options such as padding
                            layout: {
                                padding: {
                                    left: layoutPaddingLeft,
                                    right: 0,
                                    top: 0,
                                    bottom: 0
                                }
                            }
                        }}
                        data={Data}
                        redraw />
                </Suspense>}
        </div>;
    }
}
// -------------------------------------------------------------------------------------------------
export interface LineChartSettingsState {
    widgetData: LineChartData;
}
// -------------------------------------------------------------------------------------------------
export class LineChartSettings extends Component<IWidgetSettingsProps<LineChartData>, LineChartSettingsState> {
    // --------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<LineChartData>) {
        super(props);
        this.state = {
            widgetData: _.cloneDeep(this.props.WidgetData),
        };
    }
    // --------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<LineChartData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: "Y Axis Begin at Zero",
                        tooltip: 'Used to specify whether the y axis will range based on min/max values of the chart. If enabled, the y axis will show value 0.',
                        dataPath: "axisBeginAtZero"
                    }
                ]
            },
            {
                name: "Toolbar",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Toolbar Custom Icons",
                        tooltip: "Extra custom icons in toolbar",
                        dataPath: "customToolbarIcons",
                        itemsInfo: ToolbarIconInfo_GetSettingList(),
                        createNewItem: () => {
                            return {
                                icon: "",
                                tooltip: "Icon tooltip",
                                iconType: eIconType.fontAwesome,
                                id: "Icon " + getRandomSmallKey(),
                                iconColor: "#2c2c2c",
                                nestedItems: []
                            } as ToolbarIconInfo;
                        }
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Height",
                        dataPath: "toolbarHeight",
                        minValue: 20,
                        maxValue: 80,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Icon Size",
                        dataPath: "toolbarIconSize",
                        minValue: 14,
                        maxValue: 24,
                        stepValue: 1,
                        valuePosfix: "px",
                    }, {
                        type: SettingsType.ColorPicker,
                        label: 'Default Icons Color',
                        tooltip: 'Set value for default Icons.',
                        dataPath: "defaultIconsColor",
                    },
                ]
            },
            {
                name: "Min/Max/Avg",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Min/Max/Avg',
                        tooltip: "Whether to show values' statistics or not. If enabled, the minimum, maximum and average values will be shown.",
                        dataPath: "showStats"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Min Color',
                        tooltip: 'Select the color of min value line',
                        dataPath: "minColor",
                        isHidden: (data) => !data.showStats
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Max Color',
                        tooltip: 'Select the color of max value line',
                        dataPath: "maxColor",
                        isHidden: (data) => !data.showStats
                    },
                    {
                        type: SettingsType.Slider,
                        isHidden: (data) => !data.showStats,
                        label: 'Avg Line Thickness',
                        tooltip: 'Select the thickness of the average value line',
                        dataPath: "avgLineThickness",
                        minValue: 0, maxValue: 4, stepValue: 1,
                        valuePosfix: "px"
                    } as SettingsElementSlider<LineChartData>
                ]
            },
            {
                name: "Date Range",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Date Range',
                        tooltip: 'If enabled, a date range picker will be shown in the toolbar.',
                        dataPath: "showDateRange"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Show Realtime button",
                        tooltip: 'Append RealTime switch inside widget',
                        dataPath: "showRealTimeFlag",
                        isHidden: (data: LineChartData) => !data.showDateRange
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Initial Realtime state",
                        tooltip: '',
                        dataPath: "initialRealtimeData",
                        isHidden: (data: LineChartData) => !data.showDateRange || !data.showRealTimeFlag
                    },
                    {
                        type: SettingsType.Number,
                        label: "Initial Days Period",
                        dataPath: "initialDateRangeDays",
                        tooltip: 'Specify the initial period of the range in days. Default value is set to 30 days, meaning that when you load the page, the range will span from current day to 30 days before.',
                        minValue: 0,
                        isHidden: (data: LineChartData) => !data.showDateRange,
                        oneLine: true
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Selection of dates with time",
                        tooltip: 'Whether to include time in range selection or not.',
                        dataPath: "timePicker",
                        isHidden: (data: LineChartData) => !data.showDateRange
                    },
                    {
                        type: SettingsType.Number,
                        label: "Time Picker increment (min)",
                        dataPath: "timePickerIncrement",
                        tooltip: 'In case you enable time selection, you can specify the step of the time picker in minutes.',
                        maxValue: 60,
                        minValue: 0,
                        isHidden: (data: LineChartData) => !data.showDateRange || !data.timePicker,
                        oneLine: true
                    },
                    {
                        type: SettingsType.Number,
                        label: "Initial Hour Period",
                        tooltip: "Specify the initial period of the range in hours.",
                        dataPath: "timePickerInitialRange",
                        minValue: 0,
                        isHidden: (data: LineChartData) => !data.showDateRange || !data.timePicker,
                        oneLine: true
                    }
                ]
            },
            {
                name: 'Legend',
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Legend',
                        dataPath: 'showLegend',
                        tooltip: 'If enabled, show the legend'
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Position',
                        tooltip: 'Set the position of the legend (top, bottom, left, right)',
                        dataPath: 'legendPosition',
                        items: [
                            { value: 'top', label: 'Top' },
                            { value: 'left', label: 'Left' },
                            { value: 'bottom', label: 'Bottom' },
                            { value: 'right', label: 'Right' }
                        ],
                        isHidden: data => !data.showLegend
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Use Point Style',
                        tooltip: 'Label style will match corresponding point style',
                        dataPath: 'legendUsePointStyle',
                        isHidden: data => !data.showLegend
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Font Color',
                        tooltip: 'Color of legend text',
                        dataPath: 'legendFontColor',
                        isHidden: data => !data.showLegend
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Font Size',
                        tooltip: 'Font size of legend text (9-25)',
                        dataPath: 'legendFontSize',
                        minValue: 9,
                        maxValue: 25,
                        isHidden: data => !data.showLegend,
                        oneLine: true
                    }
                ]
            },
            {
                name: "Excel Download",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Excel Download',
                        tooltip: 'Allows to download an excel spreadsheet (XLSX file) with the extracted data from the chart',
                        dataPath: 'excelDownload'
                    },
                    {
                        type: SettingsType.String,
                        label: "File Name",
                        dataPath: "excelOptions.fileName",
                        tooltip: 'Define excel file name',
                        oneLine: true,
                        isHidden: (data: LineChartData) => !data.excelDownload
                    },
                ]
            },
        ]

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                this.setState({ widgetData: data },
                    () => {
                        if (isDefined(this.props.onWidgetDataUpdate))
                            this.props.onWidgetDataUpdate(this.state.widgetData);
                    }
                )
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// ---------------------------------------------------------------------------------------------

export class LineChartWidget extends IWidgetThingsClass<LineChartData> {
    // ------------------------------------------------------------------------------------------------
    Render(): ReactNode { return <LineChart />; }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <LineChartSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    public initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing: Plegma.Thing = WidgetDescriptionToPlegmaThing(this.Description);

        // Calculate the range
        let startDate = moment().startOf('minute').subtract(wData.initialDateRangeDays, 'days');
        if (wData.timePicker)
            startDate.subtract(wData.timePickerInitialRange, "hours");
        let endDate = moment().startOf('minute');
        let dates = Formatters.datetimeRange(startDate, endDate, DateFormat.ISO, wData.timePicker, true).split(' - ');

        // Ports
        let ports: WebThingPort[] = [];
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Data, data)
        ));

        ports.push(new WebThingPort(
            {
                Name: 'Date Range',
                PortKey: PortKeys.DateRange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            data => this.ValidateValues(PortKeys.DateRange, data)
        ));

        ports.push(new WebThingPort(
            {
                Name: "Selected Data",
                PortKey: PortKeys.SelectedData,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            },
            WebThingPortType.Trigger
        ));

        ports.push(new WebThingPort(
            {
                Name: "Selected Range",
                PortKey: PortKeys.SelectedRange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: JSON.stringify({ StartDate: dates[0], EndDate: dates[1] })
            },
            WebThingPortType.Data
        ));

        ports.push(new WebThingPort(
            {
                Name: "Real Time",
                PortKey: PortKeys.RealTime,
                Type: Plegma.ePortType.Boolean,
                ioDirection: Plegma.ioPortDirection.Output,
                State: !wData.initialRealtimeData || !wData.showRealTimeFlag ? "False" : "True"
            },
            WebThingPortType.Data
        ));

        ports.push(new WebThingPort(
            {
                Name: 'onToolbarIconClick',
                PortKey: LineChartKeys.onToolbarIconClick,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            },
            WebThingPortType.Data
        ));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): LineChartData {
        return {
            showStats: false,
            minColor: 'blue',
            maxColor: 'red',
            avgLineThickness: 1,
            showDateRange: false,
            initialDateRangeDays: 30,
            timePicker: false,
            timePickerIncrement: 5,
            timePickerInitialRange: 0,
            axisBeginAtZero: false,
            initialRealtimeData: true,
            showRealTimeFlag: false,
            showLegend: true,
            legendPosition: 'top',
            legendUsePointStyle: false,
            legendFontColor: '#666',
            legendFontSize: 12,
            excelDownload: false,
            excelOptions: {
                fileName: "Data"
            },
            customToolbarIcons: [],
            toolbarHeight: 25,
            toolbarIconSize: 15
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return LineChartWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: LineChartData = LineChartWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Line Chart",
            WidgetTitle: "",
            Key: "LineChartWidget",
            Type: "LineChartWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/line_chart.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultValues(): { [key: string]: any } {
        return {
            [PortKeys.Data]: null
        };
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.Data) {
            return ChartsJsonSchemeValidator().DataValidateString(value);
        } else if (key == PortKeys.DateRange) {
            return chartDateRangeValidator().DataValidateString(value);
        }
        return { valid: true, errors: '' };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = { "RealTime": "Real Time" };
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;
        return res;
    }
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Data; };
    // ------------------------------------------------------------------------------------------------
}
// ---------------------------------------------------------------------------------------------